import { cloneDeep, isEqualWith, merge } from 'lodash-es'
import type VideoPlayer from '../../types/VideoPlayer'
import {
    getVideoState,
    setVideoMuteState,
    setVideoVolumeState,
} from '../app-state/state.video'
import { PAPER_HEIGHTS } from '../components/theme'
import {
    FloaterSize,
    UserEvent,
    VideoEvent,
    VideoType,
} from '../constants/enums'
import {
    VIDEO_BOTTOM_ROUNDED_CONTAINER_CLASSES,
    VIDEO_CONTAINER_CLASSES,
    VIDEO_CONTAINER_PREFIX_ID,
    VIDEO_ELEVATED_CONTAINER_CLASSES,
    VIDEO_FULL_ROUNDED_CONTAINER_CLASSES,
    VIDEO_PLAYER_ID,
    VIDEO_ZOOMED_CONTAINER_CLASSES,
} from '../constants/video'
import {
    VideoConfig,
    generateVideoConfig,
} from '../mfcLogic/generateVideoConfig'
import { areFunctionsEqual } from '../utilities/comparison'
import { parseStyles, stylesToString } from '../utilities/css'
import { aspectRatio } from '../utilities/dimensions'
import { floatEqual } from '../utilities/math'
import { safeObj } from '../utilities/safe'
import { pubSubUsers, pubSubVideo } from './pubSub'
import { SnapSize, registerSnapTimer } from './state.timers'
import { USERSSTATE, resolveID, slugToID } from './state.users'

const DEACTIVATE_TIMEOUT = 1500

export const videoElement = (id: number) =>
    document.getElementById(
        VIDEO_PLAYER_ID.replace('{{ID}}', String(id)),
    ) as HTMLVideoElement

export type VideoEventHandler =
    | ((this: GlobalEventHandlers, ev: MouseEvent | TouchEvent) => any)
    | null
    | undefined

export interface VideoOptions {
    allowControls?: boolean
    forceHeight?: boolean
    forefront?: boolean
    onclick?: VideoEventHandler
    onlyVolumeControls?: boolean
    pinToTop?: boolean
    autoMute?: boolean
    styles?: string
}

export interface VideoTouchListeners {
    onTouchStart?: (event: TouchEvent) => void
    onTouchMove?: (event: TouchEvent) => void
    onTouchEnd?: (event: TouchEvent) => void
}

export interface VideoDimensions {
    height: number
    width: number
}

export interface VideoCache {
    onDimensions: ((dimensions: VideoDimensions) => void) | null
    onPlaying: ((playing: boolean | null) => void) | null
    dimensions: VideoDimensions
    id: number | null
    hidden: boolean
    options: VideoOptions | null
    playing: boolean | null
    state: number | null
    sessionId: number | null
}

interface VideoPlaceholder {
    onDimensions?: ((dimensions: VideoDimensions) => void) | undefined
    onPlaying?: ((playing: boolean | null) => void) | undefined
    options: VideoOptions
    videoType: VideoType
    node: HTMLElement
    id: number
}

interface VideoInitOptions {
    allowControls?: boolean
    onlyVolumeControls?: boolean
    videoType?: VideoType
    onMute?: (mute: boolean) => void
    autoStarted?: boolean
    mute?: boolean
}

export interface VideoState {
    container: HTMLDivElement
    initOptions: VideoInitOptions
    cache: VideoCache
    unsubscribe: (() => void) | null
    config: VideoConfig | null
    id: number | null
    instance: VideoPlayer.Instance | null
    onclick?: VideoEventHandler | undefined
    placeholders: Array<VideoPlaceholder>
    autoMutedBy: Record<number, boolean>
    autoMuted: Record<number, boolean>
    wasAutoMuted: boolean
    volume: number | null
    new: boolean
    timers: {
        callback: ReturnType<typeof setTimeout> | null
        reset: ReturnType<typeof setTimeout> | null
        hide: ReturnType<typeof setTimeout> | null
        retryUser: number | null
        retrySession: number | null
        retryVideoPlayer: number | null
        raf: number | null
    }
    removeTouch?: () => void
}

export type VideoStates = Record<number, VideoState>

export const VIDEOSTATE: VideoStates = {}

export type InstanceCounter = Record<number, number>

export const INSTANCE_COUNTER: InstanceCounter = {}

type GlobalVideoCache = {
    [key in VideoType]?: {
        dimensions: VideoDimensions
    }
}

export const GLOBAL_VIDEO_CACHE: GlobalVideoCache = {
    [VideoType.Room]: { dimensions: { width: 0, height: 0 } },
    [VideoType.StickyPreview]: { dimensions: { width: 0, height: 0 } },
    [VideoType.Menu]: { dimensions: { width: 0, height: 0 } },
}

if (typeof window !== 'undefined') {
    window.__MFC_APP_VIDEO__ = VIDEOSTATE
}

const defaultState: VideoState = {
    cache: {
        dimensions: { height: 0, width: 0 },
        id: null,
        hidden: false,
        options: null,
        playing: null,
        onDimensions: null,
        onPlaying: null,
        state: null,
        sessionId: null,
    },
    onclick: undefined,
    wasAutoMuted: null,
    initOptions: null,
    unsubscribe: null,
    placeholders: [],
    container: null,
    autoMutedBy: {},
    instance: null,
    autoMuted: {},
    volume: null,
    config: null,
    new: true,
    id: null,
    timers: {
        callback: null,
        retryUser: null,
        retrySession: null,
        retryVideoPlayer: null,
        reset: null,
        hide: null,
        raf: null,
    },
}

const emitInitializing = (
    id: number,
    videoType: VideoType = VideoType.Room,
) => {
    pubSubVideo.emit(VideoEvent.INITIALIZING + '::' + videoType, id, true)
}

const emitPlaying = (id: number, playing: boolean | null) => {
    pubSubVideo.emit(VideoEvent.PLAYING + '::' + id, playing, true)
}

const emitDimensions = (videoType: VideoType, dimensions: VideoDimensions) => {
    pubSubVideo.emit(VideoEvent.DIMENSIONS + '::' + videoType, dimensions, true)
}

const setOnclick = (onclick: VideoEventHandler, id: number) => {
    const state = VIDEOSTATE[id]

    if (state.onclick === onclick) {
        return
    }

    const video = videoElement(id)
    // console.log(`[video ${id}] video element`, video)
    // console.log(`[video ${id}] video instance`, state.instance)
    if (!video) {
        return
    }
    video.ontouchstart = onclick
    video.ontouchend = onclick
    video.onclick = onclick
    state.onclick = onclick
}

const reset = (id: number) => {
    // console.log(`[video.reset ${id}]`)

    const state = VIDEOSTATE[id]

    cancelAnimationFrame(state.timers.retryUser)
    cancelAnimationFrame(state.timers.retrySession)
    cancelAnimationFrame(state.timers.retryVideoPlayer)

    // console.log(`[video.reset ${id}] callback timer`, state.timers.callback)

    clearTimeout(state.timers.callback)
    clearTimeout(state.timers.reset)
    clearTimeout(state.timers.hide)

    state.timers.callback = null
    state.timers.retryUser = null
    state.timers.retrySession = null
    state.timers.retryVideoPlayer = null
    state.timers.reset = null
    state.timers.hide = null

    if (state.unsubscribe) {
        state.unsubscribe()
    }

    if (state.instance) {
        state.instance.Disconnect()
    }

    if (state.container) {
        state.container.remove()
    }

    if (state.cache.playing) {
        state.cache.playing = null
        emitPlaying(id, null)
        state.placeholders.forEach(({ onPlaying }) => {
            onPlaying?.(null)
        })
    }

    // console.log(`[video.reset ${id}] deleting state`)
    delete VIDEOSTATE[id]
}

const hide = (id: number) => {
    // console.log(`[video ${id}] attempting to hide`)

    const state = VIDEOSTATE[id]

    const execute = () => {
        clearTimeout(state.timers.hide)
        state.timers.hide = null
        // console.log(`[video ${id}] temporarily hiding`)
        transform({ left: 0, top: 0, height: 0, width: 0 }, id)
        state.instance?.setMute(true)
        state.cache.hidden = true
    }

    clearTimeout(state.timers.hide)
    state.timers.hide = setTimeout(execute, 250)
}

const runAnimationFrame = (id: number) => () => {
    const state = VIDEOSTATE[id]

    cancelAnimationFrame(state.timers.raf)
    state.timers.raf = requestAnimationFrame(runAnimationFrame(id))

    const placeholder = state.placeholders[state.placeholders.length - 1]
    if (!placeholder) {
        return
    }

    const { node, options, onPlaying, onDimensions, videoType } = placeholder
    if (!node) {
        return
    }

    clearTimeout(state.timers.hide)
    state.timers.hide = null

    if (state.cache.hidden && state.instance) {
        // console.log(`[video ${id}] unhiding`)
        const { mute } = getVideoState()

        // console.log(`[video ${id}] setting mute state (hidden video)`)

        state.instance.setMute(
            state.initOptions?.mute !== undefined
                ? state.initOptions?.mute
                : mute,
        )

        state.cache.hidden = false
    }

    const nodeRect = node.getBoundingClientRect()
    const {
        height: nodeHeight,
        width: nodeWidth,
        left: nodeLeft,
        top: nodeTop,
    } = nodeRect

    const {
        height: videoHeight,
        width: videoWidth,
        left: videoLeft,
        top: videoTop,
    } = state.container.getBoundingClientRect()

    const {
        onlyVolumeControls = false,
        allowControls = true,
        forceHeight,
        onclick,
    } = safeObj(options)

    const { height: calcHeight, width: calcWidth } = aspectRatio(nodeRect)
    const width = !forceHeight ? calcWidth : nodeWidth
    const height = !forceHeight ? calcHeight : (nodeWidth * 9) / 16
    const left = (nodeWidth - width) / 2 + nodeLeft
    const top = nodeHeight > 0 ? (nodeHeight - height) / 2 + nodeTop : nodeTop

    if (forceHeight && !floatEqual(nodeHeight, height)) {
        node.style.height = `${height}px`
    }

    if (!GLOBAL_VIDEO_CACHE[videoType]) {
        GLOBAL_VIDEO_CACHE[videoType] = {
            dimensions: { height, width },
        }
        emitDimensions(videoType, { height, width })
    } else {
        const globalCache = GLOBAL_VIDEO_CACHE[videoType]
        if (!globalCache.dimensions) {
            globalCache.dimensions = { height, width }
            emitDimensions(videoType, { height, width })
        } else {
            const globalCacheDimensionsEqual =
                floatEqual(globalCache.dimensions.height, height) &&
                floatEqual(globalCache.dimensions.width, width)
            if (!globalCacheDimensionsEqual) {
                globalCache.dimensions = { height, width }
                emitDimensions(videoType, { height, width })
            }
        }
    }

    const cacheDimensionsEqual =
        floatEqual(state.cache.dimensions.height, height) &&
        floatEqual(state.cache.dimensions.width, width)

    if (!cacheDimensionsEqual || state.cache.onDimensions !== onDimensions) {
        state.cache.dimensions = { height, width }
        state.cache.onDimensions = onDimensions
        onDimensions?.({ height, width })
    }

    if (!state.instance) {
        if (state.cache.playing) {
            state.cache.playing = null
            emitPlaying(id, null)
            onPlaying?.(null)
        }
        return
    }

    if (
        state.cache.playing !== state.instance.playing ||
        state.cache.onPlaying !== onPlaying
    ) {
        if (state.cache.playing !== state.instance.playing) {
            state.cache.playing = state.instance.playing
            emitPlaying(id, state.instance.playing)
        }
        onPlaying?.(state.instance.playing)
        state.cache.onPlaying = onPlaying
    }

    if (
        state.cache.options === options &&
        floatEqual(videoHeight, height) &&
        floatEqual(videoWidth, width) &&
        floatEqual(videoLeft, left) &&
        floatEqual(videoTop, top)
    ) {
        return
    }

    if (state.cache.options !== options) {
        setOnclick(onclick, id)
        state.instance.setAllowControls(allowControls)
        state.instance.setOnlyVolumeControls(onlyVolumeControls)
        state.cache.options = options
    }

    transform({ left, top, height, width, ...options }, id)
}

export const activateTransform = (id: number) => {
    const state = VIDEOSTATE[id]
    if (state) {
        cancelAnimationFrame(state.timers.raf)
        state.timers.raf = requestAnimationFrame(runAnimationFrame(id))
    }
}

export const deactivateTransform = (id: number) => {
    const state = VIDEOSTATE[id]
    if (state) {
        cancelAnimationFrame(state.timers.raf)
        state.timers.raf = null
    }
}

const destroy = (id: number) => {
    if (!VIDEOSTATE[id]) return
    // console.log(`[video ${id}] destroy`)
    deactivateTransform(id)
    reset(id)
}

export const addVideoTouchListeners = (
    id: number,
    listeners: VideoTouchListeners,
) => {
    removeVideoTouchListeners(id)
    const state = VIDEOSTATE[id]
    if (!state) return
    // console.log('[addVideoTouchListeners]', id, state)
    const { onTouchEnd, onTouchMove, onTouchStart } = listeners
    state.container.addEventListener('touchstart', onTouchStart)
    state.container.addEventListener('touchmove', onTouchMove)
    state.container.addEventListener('touchend', onTouchEnd)
    state.removeTouch = () => {
        if (state.container) {
            state.container.removeEventListener('touchstart', onTouchStart)
            state.container.removeEventListener('touchmove', onTouchMove)
            state.container.removeEventListener('touchend', onTouchEnd)
        }
    }
}
export const removeVideoTouchListeners = (id: number) => {
    const state = VIDEOSTATE[id]
    // console.log('[removeVideoTouchListeners]', id, state)
    state?.removeTouch?.()
}

const setupDependencies = (id: number) => {
    const existingVideoState = VIDEOSTATE[id]
    if (existingVideoState) {
        clearTimeout(existingVideoState.timers.reset)
        existingVideoState.timers.reset = null
        return existingVideoState
    }

    const container = document.createElement('div')
    container.className = VIDEO_CONTAINER_CLASSES
    container.id = VIDEO_CONTAINER_PREFIX_ID + id
    document.body.appendChild(container)

    const state = cloneDeep(defaultState)
    state.container = container
    VIDEOSTATE[id] = state
    return state
}

export const enableRoundedVideo = (
    id: number,
    rounded = true,
    elevated = false,
    size = FloaterSize.Small,
    zoomVideo = false,
) => {
    // console.log('[enableRoundedVideo]', id, { rounded, elevated, size })
    const existingVideoState = VIDEOSTATE[id]
    if (existingVideoState && existingVideoState.container) {
        let combinedClasses = VIDEO_CONTAINER_CLASSES

        if (rounded) {
            if (size > FloaterSize.Small) {
                combinedClasses += VIDEO_BOTTOM_ROUNDED_CONTAINER_CLASSES
            } else {
                combinedClasses += VIDEO_FULL_ROUNDED_CONTAINER_CLASSES
            }
        }
        if (zoomVideo) {
            combinedClasses += VIDEO_ZOOMED_CONTAINER_CLASSES
        }
        if (elevated) {
            combinedClasses += VIDEO_ELEVATED_CONTAINER_CLASSES
        }
        existingVideoState.container.className = combinedClasses
    }
}

export const register = (placeholder: VideoPlaceholder) => {
    const { options, id } = placeholder
    const { autoMute, onlyVolumeControls, allowControls } = options
    const hasExistingState = !!VIDEOSTATE[id]

    // console.log(`[video ${id}] register`, { placeholder, hasExistingState })
    // removeVideoTouchListeners(id)

    //
    // WHY IS VIDEOSTATE UNDEFINED???
    //
    // 1. Direct load of a room (SSR), where Video mounts and calls `register`
    // before Room invokes `openRoom` (where `initVideo` is called).
    //
    // 2. If the user's internet is slow enough and the user is navigating
    // to a page that has yet to be lazy loaded, `initVideo` might have
    // already timed out.
    //
    // So we start the video again without "user" gesture, so it will be
    // muted with an overlay just like SSR, but that's better than leaving
    // the user with just snapshots.
    //
    const state = hasExistingState
        ? setupDependencies(id)
        : initVideo(id, {
              onlyVolumeControls,
              autoStarted: true,
              allowControls,
          })

    // const state = setupDependencies(id)

    state.placeholders.push(placeholder)

    if (autoMute) {
        // console.log(`[video ${id}] register: attempting to automute`)

        Object.values(VIDEOSTATE).forEach((otherState) => {
            if (
                otherState.id === id ||
                !otherState.instance ||
                otherState.instance?.player.muted()
            ) {
                // console.log(
                //     `[video ${id}] register: skipping mute state`,
                //     '\n',
                //     'otherState.id',
                //     otherState.id,
                //     '\n',
                //     'otherState.instance',
                //     !!otherState.instance,
                //     '\n',
                //     'otherState.instance?.player.muted()',
                //     otherState.instance?.player?.muted(),
                // )
                return
            }

            state.autoMuted[otherState.id] = true
            state.autoMutedBy[id] = true
            otherState.wasAutoMuted = true

            // console.log(`[video ${id}] register: setting mute state`)

            otherState.instance.setMute(true)
        })

        // console.log(
        //     `[video ${id}] register: automuted`,
        //     JSON.stringify(state.autoMuted, null, 4),
        // )
    }

    if (state.placeholders.length > 1) {
        return
    }

    activateTransform(id)
}

export const unregister = (targetNode: HTMLElement, id: number) => {
    const state = VIDEOSTATE[id]
    if (!state) return
    const autoMuted = Object.keys(state.autoMuted)

    // console.log(
    //     `[video ${id}] unregister: automuted`,
    //     JSON.stringify(autoMuted, null, 4),
    // )

    autoMuted.forEach((otherID) => {
        const otherState = VIDEOSTATE[otherID]

        if (!otherState) {
            return
        }

        delete state.autoMuted[otherID]
        delete otherState.autoMutedBy[id]
        otherState.wasAutoMuted = false

        // console.log(
        //     `[video ${id}] unregister: attempting to set mute state of ${otherID} "false". Hidden: ${otherState.cache.hidden}`,
        // )

        if (!otherState.cache.hidden) {
            // console.log(
            //     `[video ${id}] unregister: setting mute state of ${otherID} "false"`,
            // )
            otherState.instance.setMute(false)
        }
    })

    state.cache.options = null
    state.placeholders = state.placeholders.filter(
        ({ node }) => node !== targetNode,
    )

    if (state.placeholders.length > 0) {
        // don't destroy the video if there are other video instances playing for this id
        return
    }

    hide(id)
    deactivateTransform(id)
}

const destroyStaleEntries = () => {
    Object.entries(VIDEOSTATE).forEach(([idStr, state]) => {
        if (idStr.startsWith('transitioning_')) {
            // console.log(`[video ${idStr}] found transitioning instance`)

            const [, , id] = idStr.split('_')
            const newState = VIDEOSTATE[id]

            if (newState && !newState?.instance.playing && state.new) {
                //
                // ensure state remains untouched for at least the duration of
                // one round of "DEACTIVATE_TIMEOUT"
                //
                state.new = false
                // console.log(`[video ${idStr}] not ready to transition yet`)
                return
            }

            destroyTransitionInstance(state, idStr)

            return
        }

        if (state.placeholders.length > 0) {
            return
        }

        if (state.new) {
            //
            // ensure state remains untouched for at least the duration of
            // one round of "DEACTIVATE_TIMEOUT"
            //
            state.new = false
            return
        }

        const id = Number(idStr)

        clearTimeout(state.timers.reset)
        state.timers.reset = setTimeout(() => {
            clearTimeout(state.timers.reset)
            state.timers.reset = null
            // console.log(`[video ${id}] resetting from timeout`)
            if (state.placeholders.length > 0) {
                // console.log(`[video ${id}] resetting from timeout no more`)
                return
            }

            // console.log(`[video ${id}] no placeholders, resetting from timeout`)
            destroy(id)
        }, DEACTIVATE_TIMEOUT)
    })
}

const startStateCleanupInterval = () => {
    setInterval(destroyStaleEntries, DEACTIVATE_TIMEOUT * 2)
}

if (typeof window !== 'undefined') {
    startStateCleanupInterval()
}

interface VideoTransformConfig {
    forefront?: boolean
    pinToTop?: boolean
    styles?: string
    height: number
    width: number
    left: number
    top: number
}

// TODO: maybe we should just call this directly on room size updates???
// all the situations where the video size/location should be adjusted are
// handled, so it should be possible to call transform there so the video size/location
// would be updated more quickly (no rendering in wrong spot for a frame)

export const transform = (config: VideoTransformConfig, id: number) => {
    const {
        forefront,
        pinToTop,
        styles,
        height = 0,
        width = 0,
        left = 0,
        top: y = 0,
    } = config
    const top = pinToTop ? window.visualViewport.pageTop || window?.scrollY : y
    const zIndex = PAPER_HEIGHTS.videoForefront

    const style =
        `height:${height || 0}px;` +
        `width:${width || 0}px;` +
        `left:${left}px;` +
        `top:${top}px;` +
        (forefront ? `z-index:${zIndex};` : '') +
        stylesToString(parseStyles(styles))

    const state = VIDEOSTATE[id]

    state.container.setAttribute('style', style)
}

const destroyTransitionInstance = (state, id) => {
    // console.log(`[video ${id}] destroying previous instance`)

    if (state.instance) {
        state.instance.Disconnect()
        state.instance = null
    }

    if (state.container) {
        state.container.remove()
        state.container = null
    }

    delete VIDEOSTATE[id]
}

enum PlacardTypes {
    LOADING = 'loading',
    RESTART = 'restart',
    GROUP = 'group',
    PRIVATE = 'private',
    TRUE_PRIVATE = 'true_private',
    CLUB = 'club',
    BANNED = 'banned',
    AWAY = 'away',
    OFFLINE = 'offline',
}

const placardTypes = [
    PlacardTypes.LOADING,
    PlacardTypes.RESTART,
    PlacardTypes.GROUP,
    PlacardTypes.PRIVATE,
    PlacardTypes.TRUE_PRIVATE,
    PlacardTypes.CLUB,
    PlacardTypes.BANNED,
    PlacardTypes.AWAY,
    PlacardTypes.OFFLINE,
]

export const initVideo = (
    rawSlugOrId: string | number,
    options: VideoInitOptions = {},
) => {
    if (typeof rawSlugOrId !== 'string' && typeof rawSlugOrId !== 'number') {
        console.trace(
            '[video] Slug or user ID is required! rawSlugOrId:' + rawSlugOrId,
        )
        throw new Error(
            '[video] Slug or user ID is required! rawSlugOrId:' + rawSlugOrId,
        )
    }

    const id = resolveID(rawSlugOrId)

    if (!id) {
        console.error('[video] unable to resolve ID', rawSlugOrId)
        return
    }

    registerSnapTimer(id, SnapSize.large)
    // console.log(`[video ${id}] init`, rawSlugOrId, options)
    const state = setupDependencies(id)

    if (state.id === id) {
        // console.log(
        //     `[video ${id}] reusing cached info`,
        //     JSON.stringify(options, null, 4),
        // )
        state.initOptions = options
        //
        //
        // Todo: Look into why "initVideo" is being called more than once in SSR
        // room openings or even regularly opening a room
        //
        //
        if (options.mute === undefined && state.instance) {
            const { mute } = getVideoState()

            // console.log(
            //     `[video ${id}] setting mute state (initVideo option). Muted: ${mute}`,
            // )

            state.instance.setMute(mute)
        }
        return state
    }

    // console.log(
    //     `[video ${id}] initializing new video instance`,
    //     JSON.stringify(options, null, 4),
    // )

    state.id = id
    state.initOptions = options
    emitInitializing(id, options.videoType)

    const { autoStarted } = options
    const callback = () => {
        const state = VIDEOSTATE[id]

        if (!state) {
            console.trace(
                `[video ${id}] state is undefined in the config callback`,
            )
            return
        }

        cancelAnimationFrame(state.timers.retryUser)
        cancelAnimationFrame(state.timers.retrySession)
        cancelAnimationFrame(state.timers.retryVideoPlayer)

        if (
            typeof window.VideoPlayer !== 'function' ||
            'VideoPlayerPlugins' in window === false
        ) {
            if (!state.timers.retryVideoPlayer) {
                console.warn(`[video ${id}] VideoPlayer not available`)
            }
            state.timers.retryVideoPlayer = requestAnimationFrame(callback)
            return
        }

        if (
            !USERSSTATE[slugToID['me']]?.session ||
            !USERSSTATE[slugToID['me']]?.auth
        ) {
            if (!state.timers.retrySession) {
                console.warn(`[video ${id}] client session not available`)
            }
            state.timers.retrySession = requestAnimationFrame(callback)
            return
        }

        if (!USERSSTATE[id]) {
            if (!state.timers.retryUser) {
                console.warn(`[video ${id}] user not found in state`)
            }
            state.timers.retryUser = requestAnimationFrame(callback)
            return
        }

        state.timers.retryUser = null
        state.timers.retrySession = null
        state.timers.retryVideoPlayer = null

        const config = generateVideoConfig(id, window.VideoPlayer)
        if (!config) {
            console.error(
                `[video ${id}] user not found in state.` +
                    'This should never happen, if it does, please investigate.',
            )
            return
        }

        const { sessionId: previousSessionId, ...previousConfig } =
            state.config || {}

        const { sessionId: incomingSessionId, ...incomingConfig } = config

        const configEqual = isEqualWith(
            previousConfig,
            incomingConfig,
            areFunctionsEqual,
        )

        // const player = state.instance?.player as any
        // console.log(
        //     `[video ${id}]:`,
        //     state.instance?.getCurrentSource(),
        //     state.instance?.getCurrentSourceName(),
        //     player?.tech_?.currentSource_,
        //     typeof player?.tech_?.currentSrc === 'function'
        //         ? player?.tech_?.currentSrc()
        //         : player?.tech_?.currentSrc,
        //     JSON.stringify(state.config, null, 4),
        //     JSON.stringify(config, null, 4),
        // )

        if (configEqual || !config.videoDomain || !config.modelFlags) {
            // console.log(`[video ${id}] no relevant config update found!`)
            return
        }

        const resolveInstance = () => {
            if (INSTANCE_COUNTER[id] === undefined) {
                INSTANCE_COUNTER[id] = 0
            } else {
                INSTANCE_COUNTER[id]++
            }

            const { state: modelState } = config
            const transitionID =
                'transitioning_' + INSTANCE_COUNTER[id] + '_' + id

            if (state?.instance && state.cache.state === modelState) {
                // console.log(`[video ${id}] transitioning`)

                state.instance.changeId(transitionID)

                VIDEOSTATE[transitionID] = state

                delete VIDEOSTATE[id]

                state.container.id = 'transitioning_' + state.container.id

                const newState = setupDependencies(id)

                const {
                    placeholders,
                    unsubscribe,
                    container: __,
                    instance: _,
                    ...other
                } = state

                VIDEOSTATE[id] = merge(newState, other)

                // console.log(
                //     `[video ${id}] timers:`,
                //     JSON.stringify(VIDEOSTATE[id].timers, null, 4),
                // )

                VIDEOSTATE[id].placeholders = placeholders
                VIDEOSTATE[id].unsubscribe = unsubscribe

                state.unsubscribe = null

                VIDEOSTATE[id].cache.options = cloneDeep(state.cache.options)
            }

            if (state.cache.state !== modelState) {
                state.cache.state = modelState

                if (state.instance) {
                    // console.log(`[video ${id}] reusing container`)
                    state.instance.Disconnect()
                    state.instance = null
                }
            }

            const newState = VIDEOSTATE[id]
            const { onlyVolumeControls = false, allowControls = true } = options
            const { volume, mute } = getVideoState()

            newState.volume = volume

            const videoPlayerConfig = {
                ...config,
                mute: options.mute !== undefined ? options.mute : mute,
                containerEl: newState.container,
                // logFn: (...msg) => console.log('***video***', ...msg),
                cacheModelVolume: false,
                onlyVolumeControls,
                allowControls,
            }

            // console.log(
            //     `[video ${id}] configuration${!state.config ? '' : ' changed'}`,
            //     videoPlayerConfig,
            //     // JSON.stringify(videoPlayerConfig, null, 4),
            // )

            const instance = new window.VideoPlayer(videoPlayerConfig)
            if (!instance) {
                console.error(`[video ${id}] failed to create instance`)
                return
            }

            newState.config = config
            newState.instance = instance

            // console.log(`[video ${id}] created instance`)

            // Override the default VideoPlayerJS placards with a transparent
            // image to allow our Preview Poster images to show through.
            placardTypes.forEach(
                (type) => (instance.platform.placards[type] = 'clear.gif'),
            )

            instance.setOnPlayingCallback(() => {
                if (newState !== state) {
                    destroyTransitionInstance(state, transitionID)
                }

                newState.cache.playing = instance.playing
                emitPlaying(id, instance.playing)
                const placeholder =
                    newState.placeholders[newState.placeholders.length - 1]
                if (placeholder && placeholder.onPlaying) {
                    placeholder.onPlaying(instance.playing)
                }
            })

            instance.setOnVolumeChangeCallback(() => {
                if (
                    newState.cache.hidden ||
                    newState.timers.hide ||
                    newState.wasAutoMuted ||
                    newState.instance?.playing === false
                ) {
                    // console.log(
                    //     `[video ${id}] skipping volume/mute change. `,
                    //     `state.cache.hidden: ${state.cache.hidden}. `,
                    //     `state.timers.hide: ${state.timers.hide}. `,
                    //     `state.wasAutoMuted: ${state.wasAutoMuted}. `,
                    //     `state.instance?.playing: ${state.instance?.playing}`,
                    // )
                    if (newState.wasAutoMuted) {
                        newState.wasAutoMuted = false
                    }
                    return
                }

                // console.log(`[video ${id}] attempting to remove autoMutedBy`)

                Object.keys(newState.autoMutedBy).forEach((otherID) => {
                    const otherState = VIDEOSTATE[otherID]

                    delete otherState.autoMuted[newState.id]
                    delete newState.autoMutedBy[otherID]

                    // console.log(
                    //     `[video ${id}] removed autoMutedBy`,
                    //     JSON.stringify(otherState.autoMuted, null, 4),
                    //     JSON.stringify(newState.autoMutedBy, null, 4),
                    // )
                })

                const { volume } = newState

                if (typeof volume === 'number') {
                    newState.volume = null
                    instance.setVolume(volume)
                }

                const { initOptions: options } = newState

                if (typeof options.onMute === 'function') {
                    options.onMute(instance.player.muted())
                } else if (options.mute === undefined) {
                    setVideoMuteState(instance.player.muted())
                }

                if (!instance.player.muted()) {
                    setVideoVolumeState(instance.player.volume())
                }
            })
            // console.log(`[video ${id}] calling instance.render`)

            instance.render({ append: false })
        }

        clearTimeout(state.timers.callback)

        //
        //
        // Marc: If the state of the model's stream has changed, we should
        // destroy and initiate the next stream right away
        //
        //
        if (
            (!autoStarted && state.cache.state !== config.state) ||
            (state?.instance && !state?.instance.playing)
        ) {
            // console.log(
            //     `[video ${id}] running immidiate config update`,
            //     state.cache.state,
            //     config.state,
            // )
            resolveInstance()
            return
        }

        //
        //
        // Marc: Adding some debounce here to avoid quick configuration changes
        // from unnecessarily destroying and initializing videos
        //
        //
        // console.log(`[video ${id}] scheduling debounced config update`)

        state.timers.callback = setTimeout(() => {
            // console.log(`[video ${id}] running debounced config update`)
            resolveInstance()
        }, 1000)
    }
    state.unsubscribe = pubSubUsers.on(UserEvent.UPDATE + '::' + id, callback)

    callback()
    return state
}
